import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Divider,
  Input,
} from '@heroui/react';
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { useAuth } from '../contexts/AuthContext';
import oauthService from '../services/oauth';

const Login: React.FC = () => {
  const pageTitle = 'Sign In';
  const pageDescription = 'Sign in to your CodedGhost Minis account.';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Helper function to get redirect URL from multiple sources
  const getRedirectUrl = useCallback((): string => {
    // Check URL search parameters first (e.g., ?redirect=/checkout)
    // Try React Router's location.search first
    let searchParams = new URLSearchParams(location.search);
    let redirectParam = searchParams.get('redirect');

    // Fall back to window.location.search (for tests and edge cases)
    if (!redirectParam && typeof window !== 'undefined' && window.location) {
      searchParams = new URLSearchParams(window.location.search);
      redirectParam = searchParams.get('redirect');
    }

    if (redirectParam) {
      return redirectParam;
    }

    // Fall back to location state (e.g., from React Router's state)
    const stateRedirect = (location.state as any)?.from?.pathname;
    if (stateRedirect) {
      return stateRedirect;
    }

    // Default to home page
    return '/';
  }, [location.search, location.state]);

  // Form validation functions
  const validateEmail = (email: string): string => {
    if (!email) return 'Email is required';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return 'Invalid email format';
    return '';
  };

  const validatePassword = (password: string): string => {
    if (!password) return 'Password is required';
    if (password.length < 6) return 'Password must be at least 6 characters';
    return '';
  };

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const redirectUrl = getRedirectUrl();
      navigate(redirectUrl, { replace: true });
    }
  }, [getRedirectUrl, isAuthenticated, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setEmailError('');
    setPasswordError('');

    // Validate form
    const emailValidationError = validateEmail(email);
    const passwordValidationError = validatePassword(password);

    if (emailValidationError || passwordValidationError) {
      setEmailError(emailValidationError);
      setPasswordError(passwordValidationError);
      setIsLoading(false);
      return;
    }

    try {
      await login({ email, password });
      const redirectUrl = getRedirectUrl();
      navigate(redirectUrl, { replace: true });
      toast.success('Welcome back!');
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        'Login failed. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setIsPasswordVisible(!isPasswordVisible);
  };

  const handleOAuthButtonClick = async (providerId: string) => {
    try {
      const returnUrl = getRedirectUrl();
      await oauthService.initiateLogin(providerId, returnUrl);
    } catch (err: any) {
      const errorMessage = err.message || 'OAuth login failed';
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  return (
    <>
      <SEOHead title={pageTitle} description={pageDescription} />
      <div className='min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8'>
        <div className='max-w-md w-full space-y-8'>
          <div className='text-center'>
            <h1 className='text-3xl font-bold text-gray-900'>
              Sign in to your account
            </h1>
            <p className='mt-2 text-sm text-gray-600'>
              Or{' '}
              <Link
                to='/register'
                className='font-medium text-primary hover:text-primary-500'
              >
                create a new account
              </Link>
            </p>
          </div>

          <Card className='mt-8'>
            <CardHeader className='pb-2'>
              <h2 className='text-xl font-semibold text-center w-full'>
                Sign In
              </h2>
            </CardHeader>
            <Divider />
            <CardBody className='space-y-6'>
              {/* OAuth Buttons */}
              <div className='space-y-3'>
                {oauthService.getAvailableProviders().map(provider => (
                  <Button
                    key={provider.id}
                    variant='bordered'
                    className='w-full'
                    startContent={
                      <div className='w-5 h-5'>
                        {provider.id === 'google' && (
                          <svg
                            viewBox='0 0 24 24'
                            className='w-5 h-5'
                            aria-hidden='true'
                          >
                            <path
                              d='M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z'
                              fill='#4285F4'
                            />
                            <path
                              d='M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z'
                              fill='#34A853'
                            />
                            <path
                              d='M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z'
                              fill='#FBBC05'
                            />
                            <path
                              d='M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z'
                              fill='#EA4335'
                            />
                          </svg>
                        )}
                        {provider.id === 'facebook' && (
                          <svg
                            viewBox='0 0 24 24'
                            className='w-5 h-5'
                            fill='#1877F2'
                            aria-hidden='true'
                          >
                            <path d='M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z' />
                          </svg>
                        )}
                      </div>
                    }
                    onClick={() => handleOAuthButtonClick(provider.id)}
                    disabled={!oauthService.isProviderConfigured(provider.id)}
                  >
                    Continue with {provider.name}
                  </Button>
                ))}

                {oauthService.getAvailableProviders().length === 0 && (
                  <div className='text-center py-4 text-gray-500'>
                    <p className='text-sm'>Social sign-in is not configured.</p>
                    <p className='text-xs'>
                      Please use email and password to sign in.
                    </p>
                  </div>
                )}
              </div>

              <div className='relative'>
                <div className='absolute inset-0 flex items-center'>
                  <div className='w-full border-t border-gray-300' />
                </div>
                <div className='relative flex justify-center text-sm'>
                  <span className='px-2 bg-white text-gray-500'>
                    Or sign in with email
                  </span>
                </div>
              </div>

              <form
                onSubmit={handleSubmit}
                className='space-y-6'
                aria-label='Login form'
              >
                {error && (
                  <div className='bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md'>
                    {error}
                  </div>
                )}

                <div>
                  <Input
                    type='email'
                    label='Email address'
                    placeholder='Enter your email'
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                    variant='bordered'
                    size='lg'
                    isInvalid={!!emailError}
                    errorMessage={emailError}
                  />
                </div>

                <div>
                  <Input
                    type={isPasswordVisible ? 'text' : 'password'}
                    label='Password'
                    placeholder='Enter your password'
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    variant='bordered'
                    size='lg'
                    isInvalid={!!passwordError}
                    errorMessage={passwordError}
                    endContent={
                      <button
                        className='focus:outline-none'
                        type='button'
                        onClick={togglePasswordVisibility}
                        aria-label={
                          isPasswordVisible ? 'Hide text' : 'Show text'
                        }
                      >
                        {isPasswordVisible ? (
                          <svg
                            className='w-5 h-5 text-gray-400'
                            fill='none'
                            stroke='currentColor'
                            viewBox='0 0 24 24'
                          >
                            <path
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              strokeWidth={2}
                              d='M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21'
                            />
                          </svg>
                        ) : (
                          <svg
                            className='w-5 h-5 text-gray-400'
                            fill='none'
                            stroke='currentColor'
                            viewBox='0 0 24 24'
                          >
                            <path
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              strokeWidth={2}
                              d='M15 12a3 3 0 11-6 0 3 3 0 016 0z'
                            />
                            <path
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              strokeWidth={2}
                              d='M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z'
                            />
                          </svg>
                        )}
                      </button>
                    }
                  />
                </div>

                <Button
                  type='submit'
                  color='primary'
                  size='lg'
                  className='w-full'
                  isLoading={isLoading}
                  disabled={isLoading}
                >
                  {isLoading ? 'Signing in...' : 'Sign in'}
                </Button>
              </form>

              <div className='text-center'>
                <Link
                  to='/forgot-password'
                  className='text-sm text-primary hover:text-primary-500'
                >
                  Forgot your password?
                </Link>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    </>
  );
};

export default Login;
